import { useEffect, useRef } from 'react';

/**
 * Short enough that a status change shows within seconds. The backend caches its prefix and index
 * scans for 60 seconds (`PrefixAgeReader`, `IndexCounter`, `IndexActivityReader`), so most ticks
 * read the cache, and those counts can still be up to a minute old.
 */
export const POLL_MS = 5_000;

/**
 * Calls `load` every `POLL_MS` while `enabled`. It skips a tick while the tab is hidden or a
 * native `<dialog>` is open (`lib/ask.tsx`), so the rows under the operator do not change during
 * a decision or a click. When the tab becomes visible again it loads once and restarts the interval.
 */
export function usePoll(load: () => void, enabled = true) {
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    // A refresh between pointer down and up can move the button, and the click then misses it.
    let pointerDown = false;
    const down = () => (pointerDown = true);
    const up = () => (pointerDown = false);
    const run = () => {
      if (!pointerDown && !document.querySelector('dialog[open]')) loadRef.current();
    };
    const start = () => {
      clearInterval(timer);
      timer = setInterval(run, POLL_MS);
    };
    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timer);
        return;
      }
      run();
      start();
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('pointercancel', up, true);
    return () => {
      clearInterval(timer);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('pointercancel', up, true);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);
}
